import Form from "@rjsf/mui";
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

import React, { useState, useEffect, useRef } from 'react';
import { TextInputI18N } from '../equipments/I18NText'
import validator from "@rjsf/validator-ajv6";
import EquipmentTypeSelect from '../equipments/EquipmentTypeSelect'
import BrandSelect from '../equipments/BrandSelect'
import CountrySelect from '../equipments/CountrySelect'
import { useNotify } from 'react-admin';

import { SDK, AppService } from 'src/ts-axios-sdk/SDK'

interface Props {
    eqId: number;
    callback: any;
}

interface State {
    schema: any;
    uiSchema: any;
    formData: any;
    fields: any;
}

const theme = createTheme({
    // remove shadows that would wrap around the catalogue files, leaving the upload input outside
    shadows: ['none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none'],
    components: {
          MuiTextField: {
            defaultProps: {
              variant: 'standard'
            },
        }
    }});

const JsonSchemaForm = (props: Props) => {

    const notify = useNotify();
    const [state, setState] = useState<State>({ schema: {}, uiSchema: {}, formData: {}, fields: {} });
    const [formAlert, setFormAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [saving, setSaving] = useState(false);
    const alertRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (formAlert && alertRef.current) {
            alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [formAlert]);

    const [eqTypeId, setEqTypeId] = useState<any>(null);
    const [brandId, setBrandId] = useState<any>(null);
    const [countryId, setCountryId] = useState<any>(null);
    const [name, setName] = useState<any>('');
    const [published, setPublished] = useState<any>(false);
    const [image, setImage] = useState<any>(null);

    useEffect(() => {

        // effect function can't be async, so we create a local function
        const fetchEquipment = async (id: number | undefined) => {

            const api = AppService.EquipmentApiFactory(...SDK.Config('en'));
            await api.equipmentRead(Number(id))
                .then(async (response) => {
                    setEqTypeId(response.data.equipmentTypeId);
                    setBrandId(response.data.brandId);
                    setName(response.data.name);
                    setPublished(response.data.published);
                    setCountryId(response.data.madeInCountryId);
                    setImage(response.data.image);
                });
        }

        // and call it afterwards
        if (!isNaN(props.eqId)) {
            fetchEquipment(props.eqId).catch(console.error);
        }

    }, []) // empty deps array: run just once

    useEffect(() => {

        // effect function can't be async, so we create a local function
        const fetchEqType = async () => {

            if (eqTypeId !== null) {

                const eqTypeApi = AppService.EquipmentTypeApiFactory(...SDK.Config('en'));
                const eqApi = AppService.EquipmentApiFactory(...SDK.Config('en'));

                // full read + write form
                const jsonSchema = await eqTypeApi.equipmentTypeGetJsonSchema(eqTypeId, 'fullReadWrite');
                const eq = isNaN(props.eqId) ? null : await eqApi.equipmentRead(props.eqId);

                setState({
                    schema: jsonSchema.data.jsonSchema,
                    formData: eq?.data.jsonData ?? {},
                    uiSchema: jsonSchema.data.uiSchema,
                    fields: {
                        'TextInputI18N': TextInputI18N,
                        'AttachmentField': AttachmentField
                    }
                })
            }
            else {
                // no equipment selected: reset state
                setState({ schema: {}, uiSchema: {}, formData: {}, fields: {} })
            }
        }

        // and call it afterwards
        fetchEqType().catch(console.error);

    }, [eqTypeId]) // deps array: effect depends on eqTypeId - when it changes, effect is triggered

    const onSubmit = async (data: any) => {

        if (!name) // simple validation
        {
            setFormAlert({ type: 'error', message: 'Name is required' });
            return;
        }
        
        setSaving(true);
        setFormAlert(null);
        
        try {
            const api = AppService.EquipmentApiFactory(...SDK.Config('en'));
            
            // Enrich image with required metadata if present
            const enrichedImage = image ? {
                ...image,
                entityId: props.eqId?.toString() || "0",
                entityType: "Equipment",
                key: "Image"
            } : null;
            
            const equipmentDto = {
                id: props.eqId,
                equipmentTypeId: eqTypeId,
                brandId: brandId,
                madeInCountryId: countryId,
                name: name,
                published: published,
                image: enrichedImage,
                jsonData: data.formData
            } as AppService.EquipmentFormDTO;

            await api.equipmentCreate(equipmentDto);
            
            // Success
            setFormAlert({ type: 'success', message: 'Equipment saved successfully!' });
            
            // Delay redirect to allow user to see feedback
            setTimeout(() => {
                console.log('[JsonSchemaForm] redirecting...');
                props.callback();
            }, 1500);
            
        } catch (error: any) {
            // Extract error message from API response
            const errorMessage = error?.response?.data?.errors?.[0]?.meta?.message 
                || error?.response?.data?.errors?.[0]?.title
                || error?.message 
                || 'Failed to save equipment';
            
            setFormAlert({ type: 'error', message: errorMessage });
            
        } finally {
            setSaving(false);
        }
    };

    const onError = (errors:any) => alert(errors);

    // replaces typical array text inputs with readonly <a>'s
    const AttachmentField = (props:any) => <a href={props.formData['Src']} target='_blank'>{props.formData['Title']}</a>
const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result as string;
                setImage({
                    src: base64String,
                    title: file.name,
                    base64: base64String
                });
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <>
            <EquipmentTypeSelect externalCallback={(_e: any, v: any) => setEqTypeId(v?.id)} value={eqTypeId} />
            <TextField id="name" label="Name" variant="filled" required value={name || ''} onChange={(e: any) => setName(e.target.value)} />
            <BrandSelect externalCallback={(_e: any, v: any) => setBrandId(v?.id)} value={brandId} />
            <CountrySelect externalCallback={(_e: any, v: any) => setCountryId(v?.id)} value={countryId} />
            
            <div style={{ margin: '20px 0' }}>
                <label htmlFor="image-upload" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Image
                </label>
                {image?.src && (
                    <div style={{ marginBottom: '10px' }}>
                        <img src={image.src} alt={image.title || 'Equipment'} style={{ maxWidth: '200px', maxHeight: '200px' }} />
                    </div>
                )}
                <input
                    id="image-upload"
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg"
                    onChange={handleImageChange}
                    style={{ display: 'block' }}
                />
            </div>

            <FormControlLabel control={<Switch checked={published} onChange={(e: any) => setPublished(e.target.checked)} />} label="Published" />
            <ThemeProvider theme={theme}>
                <Form validator={validator} schema={state.schema} formData={state.formData}
                    uiSchema={state.uiSchema}
                    fields={state.fields}
                    onSubmit={onSubmit}
                    onChange={e => {
                        setState({ schema: state.schema, uiSchema: state.uiSchema, formData: e.formData, fields: state.fields });
                    }} 
                    // onError={onError} // TODO: try this
                    />
            </ThemeProvider>
            {saving && !formAlert && (
                <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 2 }}>
                    Saving...
                </Alert>
            )}
            {formAlert && (
                <Alert ref={alertRef} severity={formAlert.type} onClose={() => setFormAlert(null)} sx={{ mt: 2 }}>
                    {formAlert.message}
                </Alert>
            )}
        </>
    );
}

export default JsonSchemaForm;